import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { ddpsAPI, productsAPI, weighbridgesAPI, loadsAPI } from '../services/api';
import Input from '../components/Input';
import Button from '../components/Button';
import Modal, { ConfirmModal } from '../components/Modal';
import MultiFileUpload from '../components/MultiFileUpload';
import SearchableSelect from '../components/SearchableSelect';
import DDPLineItemsEditor, { createEmptyLineItem } from '../components/DDPLineItemsEditor';
import LoadTicketLink from '../components/LoadTicketLink';
import Pagination from '../components/Pagination';
import { formatDateTime, formatWeight } from '../utils/helpers';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const ddpHistLimit = 25;

const DdpLoadHistoryPanel = ({ ddpId, active }) => {
  const { t } = useTranslation();
  const [histPage, setHistPage] = useState(1);
  const [histLoading, setHistLoading] = useState(false);
  const [histLoads, setHistLoads] = useState([]);
  const [histPagination, setHistPagination] = useState({
    total: 0,
    page: 1,
    limit: ddpHistLimit,
    totalPages: 0
  });

  useEffect(() => {
    if (!active || !ddpId) return undefined;
    let cancelled = false;
    setHistLoading(true);
    loadsAPI
      .getAll({ ddpId, page: histPage, limit: ddpHistLimit })
      .then((res) => {
        if (cancelled) return;
        setHistLoads(res.data?.loads || []);
        const p = res.data?.pagination;
        if (p) setHistPagination(p);
      })
      .catch(() => {
        if (!cancelled) {
          setHistLoads([]);
          setHistPagination({ total: 0, page: 1, limit: ddpHistLimit, totalPages: 0 });
        }
      })
      .finally(() => {
        if (!cancelled) setHistLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, ddpId, histPage]);

  useEffect(() => {
    setHistPage(1);
  }, [ddpId]);

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">{t('ddp.loadHistoryHint')}</p>

      {histLoading ? (
        <p className="text-sm text-gray-600 py-8 text-center">{t('common.loading')}</p>
      ) : histLoads.length === 0 ? (
        <p className="text-sm text-gray-600 py-8 text-center">{t('ddp.loadHistoryEmpty')}</p>
      ) : (
        <>
          <div className="overflow-x-auto border border-gray-200 rounded-md max-h-[min(70vh,640px)] overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide sticky top-0">
                <tr>
                  <th className="px-3 py-2">{t('loads.loadDate')}</th>
                  <th className="px-3 py-2">{t('loads.ticketNumber')}</th>
                  <th className="px-3 py-2">{t('loads.status')}</th>
                  <th className="px-3 py-2">{t('ddp.product')}</th>
                  <th className="px-3 py-2">{t('trucks.plateNumber')}</th>
                  <th className="px-3 py-2">{t('loads.netWeight')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {histLoads.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50/80">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                      {row.loadDate ? formatDateTime(row.loadDate) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <LoadTicketLink loadId={row.id} ticketNumber={row.ticket?.ticketNumber} />
                    </td>
                    <td className="px-3 py-2">
                      {row.status
                        ? t(`loads.statuses.${String(row.status).toLowerCase()}`, {
                            defaultValue: row.status
                          })
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-800">{row.product?.name || '—'}</td>
                    <td className="px-3 py-2 text-gray-800">{row.truck?.plateNumber || '—'}</td>
                    <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                      {row.netWeight != null
                        ? formatWeight(row.netWeight, 'ton', { noUnit: true })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {histPagination.totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                total={histPagination.total}
                page={histPagination.page}
                limit={histPagination.limit}
                totalPages={histPagination.totalPages}
                onPageChange={setHistPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Helper to format date for input field (YYYY-MM-DD)
const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeFormLineItems = (ddp) => {
  if (Array.isArray(ddp?.lineItems) && ddp.lineItems.length > 0) {
    return ddp.lineItems.map((line) => ({
      productId: line.productId || '',
      totalWeight: String(line.totalWeight ?? '')
    }));
  }
  return [createEmptyLineItem()];
};

/** Avoid `loads.transactionTypes.` when transactionType is missing; fallback for unknown enums. */
function formatDdpTransactionType(transactionType, t) {
  const raw = transactionType != null ? String(transactionType).trim() : '';
  if (!raw) return '—';
  const key = `loads.transactionTypes.${raw.toLowerCase()}`;
  const translated = t(key);
  return translated !== key ? translated : raw;
}

const DDPDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, getPeseurWeighbridges } = useAuth();
  const isPeseur = user?.role === 'PESEUR';
  const toast = useToast();
  
  const [ddp, setDdp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [approvalDescription, setApprovalDescription] = useState('');
  const [approvalWeighbridgeId, setApprovalWeighbridgeId] = useState('');
  const [rejectionDescription, setRejectionDescription] = useState('');
  const [form, setForm] = useState({
    navire: '',
    arrivingDate: '',
    manutentionNumber: '',
    weighbridgeId: '',
    documents: [],
    transactionType: 'EXPORT'
  });
  const [lineItems, setLineItems] = useState([createEmptyLineItem()]);
  const [formErrors, setFormErrors] = useState({});
  const [productOptions, setProductOptions] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [weighbridgeOptions, setWeighbridgeOptions] = useState([]);
  const [weighbridgesLoading, setWeighbridgesLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');

  const canDeleteDdp = !!ddp && ddp.status === 'pending' && (
    user?.role === 'ADMIN' || (user?.role === 'CLIENT' && ddp.operatorId === user?.id)
  );
  const canEditDdp = !!ddp && (
    (user?.role === 'ADMIN' && ddp.status === 'pending') ||
    (user?.role === 'CLIENT' && ddp.operatorId === user?.id && ddp.status === 'pending') ||
    (user?.role === 'CLIENT' && ddp.operatorId === user?.id && ddp.status === 'denied')
  );
  const canMarkCompleted = !!ddp &&
    ['ADMIN', 'MANAGER'].includes(String(user?.role || '').toUpperCase()) &&
    String(ddp.status || '').toLowerCase() === 'accepted';

  // Fetch products list
  useEffect(() => {
    let cancelled = false;
    setProductsLoading(true);
    productsAPI.getAll({ limit: 500 })
      .then(res => {
        if (!cancelled && res?.data?.products) {
          setProductOptions(
            res.data.products.map(p => ({ value: p.id, label: p.name || p.id, unit: p.unit || 'ton' }))
          );
        }
      })
      .catch(() => {
        if (!cancelled) setProductOptions([]);
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Fetch weighbridges list (peseur: assigned weighbridges only)
  useEffect(() => {
    let cancelled = false;
    if (isPeseur) {
      const wbs = getPeseurWeighbridges();
      setWeighbridgeOptions(
        wbs.map(wb => ({ value: wb.id, label: `${wb.name || wb.id}${wb.port?.name ? ` - ${wb.port.name}` : ''}` }))
      );
      setWeighbridgesLoading(false);
      return () => { cancelled = true; };
    }
    setWeighbridgesLoading(true);
    weighbridgesAPI.getAll()
      .then(res => {
        if (!cancelled && res?.data?.weighbridges) {
          setWeighbridgeOptions(
            res.data.weighbridges
              .filter(wb => wb.status === 'active')
              .map(wb => ({ value: wb.id, label: `${wb.name || wb.id}${wb.port?.name ? ` - ${wb.port.name}` : ''}` }))
          );
        }
      })
      .catch(() => {
        if (!cancelled) setWeighbridgeOptions([]);
      })
      .finally(() => {
        if (!cancelled) setWeighbridgesLoading(false);
      });
    return () => { cancelled = true; };
  }, [isPeseur, getPeseurWeighbridges]);

  useEffect(() => {
    setActiveTab('info');
  }, [id]);

  // Fetch DDP details
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ddpsAPI.getById(id)
      .then(ddp => {
        if (!cancelled) {
          setDdp(ddp);
          // Initialize form with DDP data
          setForm({
            navire: ddp.navire || '',
            arrivingDate: formatDateForInput(ddp.arrivingDate),
            manutentionNumber: ddp.manutentionNumber || '',
            weighbridgeId: ddp.weighbridgeId || '',
            documents: [], // Don't auto-fill existing documents for edit
            transactionType: ddp.transactionType || 'EXPORT'
          });
          setLineItems(normalizeFormLineItems(ddp));
        }
      })
      .catch(err => {
        if (!cancelled) {
          toast.error(err?.response?.data?.error || t('common.error'));
          navigate('/ddp/list');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, navigate, t, toast]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleEdit = () => {
    if (!canEditDdp) {
      toast.error(t('ddp.cannotEditNonPending'));
      return;
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form to original values
    setForm({
      navire: ddp?.navire || '',
      arrivingDate: formatDateForInput(ddp?.arrivingDate),
      manutentionNumber: ddp?.manutentionNumber || '',
      weighbridgeId: ddp?.weighbridgeId || '',
      documents: [],
      transactionType: ddp?.transactionType || 'EXPORT'
    });
    setLineItems(normalizeFormLineItems(ddp));
    setFormErrors({});
  };

  const handleDelete = () => {
    if (!canDeleteDdp) return;
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ddp || !canDeleteDdp) return;

    setUpdating(true);
    try {
      await ddpsAPI.delete(ddp.id);
      toast.success(t('ddp.deleteSuccess'));
      navigate('/ddp/list');
    } catch (err) {
      toast.error(err?.response?.data?.error || t('common.error'));
    } finally {
      setUpdating(false);
      setShowDeleteModal(false);
    }
  };

  const handleApprove = async () => {
    if (!ddp || ddp.status !== 'pending') return;
    setApprovalWeighbridgeId(ddp.weighbridgeId || '');
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    if (!approvalWeighbridgeId) {
      toast.error(t('ddp.portRequiredForApproval'));
      return;
    }

    setApproving(true);
    try {
      await ddpsAPI.updateStatus(id, { 
        status: 'accepted',
        weighbridgeId: approvalWeighbridgeId,
        approvalDescription: approvalDescription || undefined
      });
      toast.success(t('ddp.approveSuccess') || 'DDP approved successfully');
      // Refresh DDP data
      const updated = await ddpsAPI.getById(id);
      setDdp(updated);
      setShowApproveModal(false);
      setApprovalDescription('');
      setApprovalWeighbridgeId('');
    } catch (error) {
      console.error('Error approving DDP:', error);
      toast.error(t('common.error'));
    } finally {
      setApproving(false);
    }
  };

  const handleDeny = async () => {
    if (!ddp || ddp.status !== 'pending') return;
    setShowDenyModal(true);
  };

  const confirmDeny = async () => {
    setApproving(true);
    try {
      await ddpsAPI.updateStatus(id, { 
        status: 'denied',
        rejectionDescription: rejectionDescription || undefined
      });
      toast.success(t('ddp.denySuccess') || 'DDP denied');
      // Refresh DDP data
      const updated = await ddpsAPI.getById(id);
      setDdp(updated);
      setShowDenyModal(false);
      setRejectionDescription('');
    } catch (error) {
      console.error('Error denying DDP:', error);
      toast.error(t('common.error'));
    } finally {
      setApproving(false);
    }
  };

  const handleMarkCompleted = async () => {
    if (!canMarkCompleted) return;
    setCompleting(true);
    try {
      await ddpsAPI.updateStatus(id, { status: 'completed' });
      toast.success(t('ddp.completeSuccess', { defaultValue: 'DDP marked as completed.' }));
      const updated = await ddpsAPI.getById(id);
      setDdp(updated);
    } catch (error) {
      console.error('Error marking DDP completed:', error);
      toast.error(error?.response?.data?.error || t('common.error'));
    } finally {
      setCompleting(false);
    }
  };

  const handleUpdate = async e => {
    e.preventDefault();
    if (!ddp || !canEditDdp) {
      toast.error(t('ddp.cannotEditNonPending'));
      return;
    }

    const nextErrors = {};
    if (!form.navire.trim()) nextErrors.navire = t('ddp.navire');
    if (!form.arrivingDate) nextErrors.arrivingDate = t('ddp.arrivingDate');
    if (!form.manutentionNumber.trim()) nextErrors.manutentionNumber = t('ddp.manutentionNumber');
    if (!form.transactionType) nextErrors.transactionType = t('ddp.transactionType');
    if (!Array.isArray(lineItems) || lineItems.length === 0) {
      nextErrors.lineItems = t('ddp.lineItemsRequired');
    }
    lineItems.forEach((item, index) => {
      if (!item.productId) nextErrors[`lineItems.${index}.productId`] = t('ddp.selectProduct');
      const totalWeight = Number(item.totalWeight);
      if (!String(item.totalWeight).trim() || !Number.isFinite(totalWeight) || totalWeight <= 0) {
        nextErrors[`lineItems.${index}.totalWeight`] = t('ddp.validTotalWeight');
      }
    });
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    setUpdating(true);
    try {
      const formData = new FormData();
      formData.append('navire', form.navire);
      formData.append('arrivingDate', form.arrivingDate);
      formData.append('manutentionNumber', form.manutentionNumber);
      formData.append('weighbridgeId', form.weighbridgeId || '');
      formData.append('transactionType', form.transactionType);
      formData.append('lineItems', JSON.stringify(
        lineItems.map((item) => ({
          productId: item.productId,
          totalWeight: Number(item.totalWeight)
        }))
      ));
      
      // Add new files and descriptions
      const descriptions = [];
      form.documents.forEach((doc) => {
        if (doc.file) {
          formData.append('documents', doc.file);
          descriptions.push(doc.description || '');
        }
      });
      
      if (descriptions.length > 0) {
        formData.append('documentDescriptions', JSON.stringify(descriptions));
      }
      
      await ddpsAPI.update(id, formData);
      toast.success(t('ddp.updateSuccess'));
      setIsEditing(false);
      
      // Refresh DDP data
      const ddp = await ddpsAPI.getById(id);
      setDdp(ddp);
      setForm({
        navire: ddp.navire || '',
        arrivingDate: formatDateForInput(ddp.arrivingDate),
        manutentionNumber: ddp.manutentionNumber || '',
        weighbridgeId: ddp.weighbridgeId || '',
        documents: [],
        transactionType: ddp.transactionType || 'EXPORT'
      });
      setLineItems(normalizeFormLineItems(ddp));
      setFormErrors({});
    } catch (err) {
      const d = err?.response?.data;
      const msg =
        d?.code === 'DOCUMENT_STORAGE_LIMIT'
          ? t('ddp.storageLimitExceeded', { limitMb: d.limitMb ?? '—' })
          : d?.error || t('common.error');
      toast.error(msg);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto mt-8 px-4 sm:px-6">
        <div className="flex items-center justify-center h-64">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!ddp) {
    return (
      <div className="max-w-4xl mx-auto mt-8 px-4 sm:px-6">
        <p className="text-red-600">{t('common.error')}</p>
      </div>
    );
  }

  const getWeighbridgeLabel = (weighbridgeId) => {
    const weighbridge = weighbridgeOptions.find((wb) => wb.value === weighbridgeId);
    if (weighbridge) return weighbridge.label;
    if (ddp?.weighbridge?.name) {
      return `${ddp.weighbridge.name}${ddp.weighbridge?.port?.name ? ` - ${ddp.weighbridge.port.name}` : ''}`;
    }
    return weighbridgeId || '—';
  };

  const detailView = (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600">{t('ddp.navire')}</label>
              <p className="mt-1 text-base">{ddp.navire}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">{t('ddp.transactionType')}</label>
              <p className="mt-1 text-base">{formatDdpTransactionType(ddp.transactionType, t)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">{t('ddp.manutentionNumber')}</label>
              <p className="mt-1 text-base">{ddp.manutentionNumber || '—'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">{t('users.selectWeighbridge')}</label>
              <p className="mt-1 text-base">{getWeighbridgeLabel(ddp.weighbridgeId)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">{t('ddp.arrivingDate')}</label>
              <p className="mt-1 text-base">{ddp.arrivingDate}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">{t('ddp.derivedTotalWeight')}</label>
              <p className="mt-1 text-base">{Number(ddp.totalWeight || 0).toLocaleString('fr-FR')} t</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">{t('ddp.status')}</label>
              <p className="mt-1 text-base">
                <span className={`inline-block px-2 py-1 rounded text-sm font-medium ${
                  ddp.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  ddp.status === 'accepted' ? 'bg-green-100 text-green-800' :
                  ddp.status === 'denied' ? 'bg-red-100 text-red-800' :
                  ddp.status === 'completed' ? 'bg-slate-100 text-slate-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {t(`ddp.status.${ddp.status}`)}
                </span>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">{t('ddp.createdAt')}</label>
              <p className="mt-1 text-base">{new Date(ddp.createdAt).toLocaleDateString()}</p>
            </div>
          </div>

          <div className="pt-4 border-t">
            <label className="text-sm font-medium text-gray-600">{t('ddp.lineItems')}</label>
            <div className="mt-2 space-y-2">
              {(ddp.lineItems || []).length > 0 ? (
                ddp.lineItems.map((line, index) => (
                  <div key={line.id || `${line.productId}-${index}`} className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
                    <div className="font-medium text-gray-900">
                      {(line.productName || line.product?.name || line.productId)} / {Number(line.totalWeight || 0).toLocaleString('fr-FR')} t
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400">—</p>
              )}
            </div>
          </div>

          {ddp.approvalDescription && ddp.status === 'accepted' && (
            <div className="pt-4 border-t">
              <label className="text-sm font-medium text-gray-600">{t('ddp.approvalDescription') || 'Approval Notes'}</label>
              <p className="mt-1 text-base text-gray-700">{ddp.approvalDescription}</p>
            </div>
          )}
          {ddp.rejectionDescription && ddp.status === 'denied' && (
            <div className="pt-4 border-t">
              <label className="text-sm font-medium text-gray-600">{t('ddp.rejectionDescription') || 'Rejection Reason'}</label>
              <p className="mt-1 text-base text-gray-700">{ddp.rejectionDescription}</p>
            </div>
          )}

          {ddp.documentUrls && (
            <div className="pt-4 border-t">
              <label className="text-sm font-medium text-gray-600">{t('ddp.documents')}</label>
              <div className="mt-2 space-y-2">
                {(() => {
                  let documents = [];
                  try {
                    const parsed = JSON.parse(ddp.documentUrls);
                    if (Array.isArray(parsed)) {
                      documents = parsed;
                    } else {
                      documents = ddp.documentUrls.split(',').map(u => ({ url: u.trim(), description: '' })).filter(d => d.url);
                    }
                  } catch {
                    documents = ddp.documentUrls.split(',').map(u => ({ url: u.trim(), description: '' })).filter(d => d.url);
                  }

                  if (documents.length === 0) {
                    return <p className="text-gray-400 text-sm">{t('ddp.noDocuments')}</p>;
                  }

                  return documents.map((doc, i) => {
                    let url = doc.url;
                    if (!url.startsWith('http') && !url.startsWith('blob:')) {
                      const isHttps = window.location.protocol === 'https:';
                      const apiPort = isHttps ? '5443' : '5000';
                      const apiProtocol = isHttps ? 'https' : 'http';
                      url = `${apiProtocol}://localhost:${apiPort}${url.startsWith('/') ? url : '/' + url}`;
                    }

                    return (
                      <div key={i} className="text-sm">
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {t('ddp.documentLink', { index: i + 1 })}
                        </a>
                        {doc.description && (
                          <span className="text-xs text-gray-600 ml-2">→ {doc.description}</span>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-4 border-t">
            {canEditDdp && (
              <Button onClick={handleEdit}>
                {t('common.edit')}
              </Button>
            )}
            {canDeleteDdp && (
              <Button onClick={handleDelete} variant="danger" disabled={updating || approving}>
                {t('common.delete')}
              </Button>
            )}
            {ddp.status === 'pending' &&
              (user?.role === 'ADMIN' ||
                user?.role === 'MANAGER' ||
                (isPeseur &&
                  String(
                    (ddp?.operator?.assignedClient || ddp?.operator?.clientProfile)?.billingType || ''
                  ).toUpperCase() === 'PAYING')) && (
              <>
                <Button 
                  onClick={handleApprove}
                  disabled={approving}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {approving ? t('common.processing') || 'Processing...' : (t('ddp.approve') || 'Approve')}
                </Button>
                <Button 
                  onClick={handleDeny}
                  disabled={approving}
                  variant="danger"
                >
                  {approving ? t('common.processing') || 'Processing...' : (t('ddp.deny') || 'Deny')}
                </Button>
              </>
            )}
            {canMarkCompleted && (
              <Button
                onClick={handleMarkCompleted}
                disabled={completing || updating || approving}
                className="text-xs px-3 py-1.5 bg-slate-700 hover:bg-slate-800 text-white"
              >
                {completing
                  ? t('common.processing', { defaultValue: 'Processing...' })
                  : t('ddp.markCompleted', { defaultValue: 'Mark completed' })}
              </Button>
            )}
            <Button variant="secondary" onClick={() => navigate('/ddp/list')}>
              {t('common.back')}
            </Button>
          </div>
        </div>
  );

  return (
    <div className="max-w-4xl mx-auto mt-8 px-4 sm:px-6 pb-8">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/ddp/list')}
          className="p-2 hover:bg-gray-100 rounded-lg transition"
          title={t('common.back')}
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <h2 className="text-2xl font-bold">{t('ddp.detailTitle')}</h2>
      </div>

      {isEditing ? (
        <form onSubmit={handleUpdate} className="max-w-2xl bg-white p-6 rounded-lg shadow-sm space-y-4">
          <Input
            label={t('ddp.navire')}
            name="navire"
            value={form.navire}
            onChange={handleChange}
            required
            error={formErrors.navire}
          />
          <Input
            label={t('ddp.manutentionNumber')}
            name="manutentionNumber"
            value={form.manutentionNumber}
            onChange={handleChange}
            required
            error={formErrors.manutentionNumber}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.selectWeighbridge')}
            </label>
            <select
              name="weighbridgeId"
              value={form.weighbridgeId}
              onChange={handleChange}
              disabled={weighbridgesLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            >
              <option value="">{t('users.selectWeighbridge')}</option>
              {weighbridgeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <SearchableSelect
            label={t('ddp.transactionType')}
            name="transactionType"
            value={form.transactionType}
            onChange={handleChange}
            options={[
              { value: 'EXPORT', label: t('loads.transactionTypes.export') },
              { value: 'IMPORT', label: t('loads.transactionTypes.import') }
            ]}
            placeholder={t('ddp.selectTransactionType')}
            required
            error={formErrors.transactionType}
          />
          <DDPLineItemsEditor
            t={t}
            lineItems={lineItems}
            setLineItems={setLineItems}
            productOptions={productOptions}
            productsLoading={productsLoading}
            errors={formErrors}
          />
          <Input
            label={t('ddp.arrivingDate')}
            name="arrivingDate"
            type="date"
            value={form.arrivingDate}
            onChange={handleChange}
            required
            error={formErrors.arrivingDate}
          />
          <MultiFileUpload
            label={t('ddp.documents')}
            name="documents"
            value={form.documents}
            onChange={documents => setForm(f => ({ ...f, documents }))}
          />
          <div className="flex gap-3 pt-4">
            <Button type="submit" loading={updating}>
              {t('common.save')}
            </Button>
            <Button type="button" variant="secondary" onClick={handleCancel} disabled={updating}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex border-b border-gray-200 gap-1 px-4 pt-3">
            <button
              type="button"
              className={`px-3 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors ${
                activeTab === 'info'
                  ? 'border-blue-600 text-blue-700 bg-white'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
              onClick={() => setActiveTab('info')}
            >
              {t('ddp.viewTabInfo')}
            </button>
            <button
              type="button"
              className={`px-3 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors ${
                activeTab === 'history'
                  ? 'border-blue-600 text-blue-700 bg-white'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
              onClick={() => setActiveTab('history')}
            >
              {t('ddp.viewTabHistory')}
            </button>
          </div>
          <div className="p-6">
            {activeTab === 'info' && detailView}
            {activeTab === 'history' && <DdpLoadHistoryPanel ddpId={id} active />}
          </div>
        </div>
      )}

      {/* Approval Modal */}
      <Modal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        title={t('ddp.approveTitle') || 'Approve DDP'}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('ddp.approveConfirmMessage') || 'Are you sure you want to approve this DDP?'}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('ddp.approvalDescriptionLabel') || 'Description (optional)'}
            </label>
            <textarea
              value={approvalDescription}
              onChange={(e) => setApprovalDescription(e.target.value)}
              placeholder={t('ddp.approvalDescriptionPlaceholder') || 'Add notes about this approval...'}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.selectWeighbridge')}
            </label>
            <select
              value={approvalWeighbridgeId}
              onChange={(e) => setApprovalWeighbridgeId(e.target.value)}
              disabled={approving || weighbridgesLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('users.selectWeighbridge')}</option>
              {weighbridgeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {approvalWeighbridgeId
                ? getWeighbridgeLabel(approvalWeighbridgeId)
                : t('ddp.portRequiredForApproval')}
            </p>
            {!approvalWeighbridgeId && (
              <p className="text-xs text-amber-700 mt-1">{t('ddp.portRequiredForApproval')}</p>
            )}
          </div>
          <div className="flex gap-3 justify-end">
            <Button 
              variant="secondary" 
              onClick={() => setShowApproveModal(false)}
              disabled={approving}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={confirmApprove}
              disabled={approving || !approvalWeighbridgeId}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {approving ? t('common.processing') || 'Processing...' : (t('ddp.approve') || 'Approve')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Denial Modal */}
      <Modal
        isOpen={showDenyModal}
        onClose={() => setShowDenyModal(false)}
        title={t('ddp.denyTitle') || 'Deny DDP'}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('ddp.denyConfirmMessage') || 'Are you sure you want to deny this DDP?'}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('ddp.rejectionDescriptionLabel') || 'Reason (optional)'}
            </label>
            <textarea
              value={rejectionDescription}
              onChange={(e) => setRejectionDescription(e.target.value)}
              placeholder={t('ddp.rejectionDescriptionPlaceholder') || 'Provide a reason for denial...'}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-3 justify-end">
            <Button 
              variant="secondary" 
              onClick={() => setShowDenyModal(false)}
              disabled={approving}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={confirmDeny}
              disabled={approving}
              variant="danger"
            >
              {approving ? t('common.processing') || 'Processing...' : (t('ddp.deny') || 'Deny')}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('ddp.confirmDeleteTitle')}
        message={t('ddp.confirmDeleteMessage', { navire: ddp?.navire || '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        loading={updating}
        variant="danger"
      />
    </div>
  );
};

export default DDPDetailPage;
